'use client';

import type { ClassValue } from 'clsx';
import type { ClientProductT } from '../../../../type/product/product';
import type { StockInfo } from '../../../../product/index';
import {
  ProductAddToCart,
  ProductAddToCartLabel,
  ProductAddToCartVariant,
} from '@vs-shop/commerce-ui/productAddToCart';
import {
  ProductBuyBox,
  ProductBuyBoxActions,
  ProductBuyBoxPrice,
  ProductBuyBoxPriceLine,
  ProductBuyBoxPriceNote,
  ProductBuyBoxPriceOriginal,
  ProductBuyBoxPriceRow,
  ProductBuyBoxPrices,
  ProductBuyBoxQuantity,
  ProductBuyBoxShippingNote,
  ProductBuyBoxSubmit,
  type ProductBuyBoxErrorVariant,
} from '@vs-shop/commerce-ui/productBuyBox';
import { clsxm } from '@vs-shop/utils';
import { shouldDisplayOriginalPrice } from '@vs-shop/domain/price/getDiscountPercentageFromPrices';
import { captureCause } from '../../../../shared/utils/sentry';
import {
  getAvailableQuantityWithCartInMind,
  shouldAddToCartButtonBeDisabled,
  validateQuantity,
} from '../../../../shared/utils/stockValidation';
import { Array, Cause, Exit, Option } from 'effect';
import { useState, useTransition } from 'react';
import { useIntl } from 'react-intl';

import { getDefaultCurrency } from '../../../../config/currency';
import { promoFreeShipping } from '../../../../config/order/promo';
import { purchaseButtonClassName } from '../../../../config/purchaseButtonClass';

import ProductPanelErrorPopup from './ProductPanelErrorPopup';
import {
  ProductAmountCounter,
  ProductAmountCounterVariant,
} from '@vs-shop/ui/productAmountCounter';
import { showErrorToast } from '@vs-shop/ui/toast';
import { IconCartOutline } from '@vs-shop/ui/icons';
import { IntlMoney } from '../../../server/intl/Intl';
import ProductCouponClickable from '../../../server/product/ProductCouponClickable';
import ProductCouponStatic from '../../../server/product/ProductCouponStatic';

import { CartError } from '@vs-shop/engine/capability/cart/errors';
import { addCouponWithProductVariantsToOrderAtomFn } from '../../../../effect/atom/order/orderMutations';
import { openCartDrawerAtom } from '@vs-shop/engine/atom/ui/cartDrawer';
import { useAtomSet } from '@effect/atom-react';
import { useAddToCart, useVariantInCart } from '../../../../hook/useAddToCart';
import { useCartLinePreviewPreload, withCartLinePreview } from '../../../../hook/cartLinePreview';
import type { ProductImagePreview } from '../../../../product/productImagePreview';

type ErrorPopupState = {
  message: string;
  variant: ProductBuyBoxErrorVariant;
};

type Props = {
  product: ClientProductT;
  imagePreview: ProductImagePreview;
  stock: StockInfo;
  className?: ClassValue;
};

export default function ProductPanelAmountAndMoney({
  product,
  imagePreview,
  stock,
  className,
}: Props) {
  const { originalPrice, discountBanner, labels, price } = product;

  const intl = useIntl();
  const currency = getDefaultCurrency();

  const [amount, setAmount] = useState(1);
  const [errorPopup, setErrorPopup] = useState<Option.Option<ErrorPopupState>>(Option.none());

  const { quantityInCart, isAdding } = useVariantInCart(product.code);
  const addToCart = useAddToCart();
  const imagePath = imagePreview.kind === 'available' ? imagePreview.template : null;
  const cartRequest = withCartLinePreview(
    { product: { ...product, stock }, quantity: amount },
    imagePath,
    product.ageRestriction
  );
  const preloadHandlers = useCartLinePreviewPreload([cartRequest.preview]);

  const setIsCartDrawerOpen = useAtomSet(openCartDrawerAtom);

  const addProductVariantAndCouponToOrder = useAtomSet(addCouponWithProductVariantsToOrderAtomFn, {
    mode: 'promiseExit',
  });

  const [isPending, startTransition] = useTransition();

  const showOriginalPrice =
    originalPrice !== null &&
    originalPrice > price &&
    shouldDisplayOriginalPrice(price, originalPrice);
  const hasFreeShipping = labels.find((label) => label.code === promoFreeShipping);

  const maxOrderable = stock.eshop.maxOrderable;
  const availableQuantity = getAvailableQuantityWithCartInMind(maxOrderable, quantityInCart);

  // Use centralized logic for determining button disabled state
  const isDisabled =
    isAdding || shouldAddToCartButtonBeDisabled(maxOrderable, quantityInCart, amount, price);
  const isBusy = isPending || isAdding;

  const onAddProductToCart = () => {
    addToCart([cartRequest]);
    setAmount(1);
  };

  const onUseCoupon = () => {
    startTransition(async () => {
      const couponCode = Option.flatMap(Option.fromNullishOr(discountBanner), (banner) =>
        Option.fromNullishOr(banner.promo)
      );
      const result = await addProductVariantAndCouponToOrder({
        couponCode,
        quantity: amount,
        // issue 218 out of scope: resolved the same way the mapper used to
        // fabricate it, so this cart payload and GTM event are unchanged.
        product,
      });

      const reportFailure = (
        cause: Cause.Cause<unknown>,
        action: string,
        productAdded: boolean
      ) => {
        captureCause(cause, {
          component: 'ProductPanelAmountAndMoney',
          action,
          additionalData: {
            productCode: product.code,
            quantity: amount,
            couponCode: Option.getOrElse(couponCode, () => 'missing'),
          },
        });
        const error = Option.getOrUndefined(Cause.findErrorOption(cause));
        if (error instanceof CartError.Rejected) {
          Option.match(Array.head(error.messages), {
            onNone: () => showErrorToast(error.message),
            onSome: (message) => {
              if (productAdded) {
                showErrorToast(message);
                return;
              }
              setErrorPopup(Option.some({ message, variant: 'destructive' }));
            },
          });
          return;
        }
        showErrorToast(
          intl.formatMessage({
            id: 'error.orderMutation.generic',
            defaultMessage: 'Došlo k chybě při zpracování objednávky.',
          })
        );
      };

      Exit.match(result, {
        onFailure: (cause) => reportFailure(cause, 'Add product to cart failed', false),
        onSuccess: (outcome) => {
          setAmount(1);
          setIsCartDrawerOpen(true);
          Exit.match(outcome.coupon, {
            onFailure: (cause) =>
              reportFailure(cause, 'Apply coupon after product add failed', true),
            onSuccess: () => {},
          });
        },
      });
    });
  };

  const showCoupon =
    discountBanner !== null && (maxOrderable ?? 0) > 0 && discountBanner.style !== 'none';

  return (
    <ProductBuyBox className={clsxm(className)}>
      <ProductBuyBoxPriceRow>
        <ProductBuyBoxPrices>
          {showOriginalPrice ? (
            <ProductBuyBoxPriceOriginal>
              <IntlMoney value={{ amount: originalPrice, currency: currency }} />
            </ProductBuyBoxPriceOriginal>
          ) : null}

          {price === 0 ? (
            <ProductBuyBoxPrice>
              {intl.formatMessage({
                defaultMessage: 'Cena na dotaz',
                id: 'component.client.product.common.priceRequest',
              })}
            </ProductBuyBoxPrice>
          ) : (
            <ProductBuyBoxPriceLine>
              <ProductBuyBoxPrice>
                <IntlMoney value={{ amount: price, currency: currency }} />
              </ProductBuyBoxPrice>
              <ProductBuyBoxPriceNote>
                {intl.formatMessage({
                  id: 'component.client.product.panel.ProductPanelAmountAndMoney.vat_included',
                  defaultMessage: 'S DPH',
                })}
              </ProductBuyBoxPriceNote>
            </ProductBuyBoxPriceLine>
          )}
        </ProductBuyBoxPrices>

        {hasFreeShipping ? (
          <ProductBuyBoxShippingNote>
            {intl.formatMessage({
              id: 'product.panel.freeShipping',
              defaultMessage: 'DOPRAVA ZDARMA',
            })}
          </ProductBuyBoxShippingNote>
        ) : null}
      </ProductBuyBoxPriceRow>

      <ProductBuyBoxActions>
        <ProductBuyBoxQuantity>
          <ProductAmountCounter
            variant={ProductAmountCounterVariant.PLAIN}
            amount={amount}
            isLoading={isBusy}
            onDecrementAction={() => {
              setAmount((prev) => (prev > 1 ? prev - 1 : prev));
            }}
            onIncrementAction={() => {
              setAmount((prev) => (prev < availableQuantity ? prev + 1 : prev));
            }}
            onInputChangeAction={(value) => {
              setAmount(validateQuantity(value, availableQuantity));
            }}
            decrementDisabled={isBusy || amount <= 1}
            incrementDisabled={isBusy || amount >= availableQuantity}
            decrementLabel={intl.formatMessage({
              id: 'component.client.product.productAmountCounter.decrementLabel',
              defaultMessage: 'Snížit počet',
            })}
            incrementLabel={intl.formatMessage({
              id: 'component.client.product.productAmountCounter.incrementLabel',
              defaultMessage: 'Zvýšit počet',
            })}
            amountLabel={intl.formatMessage({
              id: 'component.client.product.productAmountCounter.amountLabel',
              defaultMessage: 'Počet',
            })}
          />
        </ProductBuyBoxQuantity>

        <ProductBuyBoxSubmit>
          <ProductAddToCart
            variant={ProductAddToCartVariant.PANEL}
            {...preloadHandlers}
            onClick={onAddProductToCart}
            className={purchaseButtonClassName}
            loading={isBusy}
            disabled={isDisabled}
          >
            <IconCartOutline className="size-4 shrink-0 @sm:size-5" />
            <ProductAddToCartLabel variant={ProductAddToCartVariant.PANEL}>
              {intl.formatMessage({
                id: 'component.client.product.panel.ProductPanelAmountAndMoney.toCart',
                defaultMessage: 'Do košíku',
              })}
            </ProductAddToCartLabel>
          </ProductAddToCart>

          {Option.isSome(errorPopup) ? (
            <ProductPanelErrorPopup
              message={errorPopup.value.message}
              onClose={() => {
                setErrorPopup(Option.none());
              }}
              variant={errorPopup.value.variant}
            />
          ) : null}
        </ProductBuyBoxSubmit>
      </ProductBuyBoxActions>

      {showCoupon && !isDisabled ? (
        <>
          {discountBanner.style === 'clickable' ? (
            <ProductCouponClickable
              showCodeName
              price={price}
              priceWithCode={discountBanner.money}
              codeName={discountBanner.promo}
              onUse={onUseCoupon}
              isLoading={isPending}
              className="mt-4"
            />
          ) : (
            <ProductCouponStatic
              price={price}
              priceWithCode={discountBanner.money}
              codeName={discountBanner.promo}
              className="mt-4"
            />
          )}
        </>
      ) : null}
    </ProductBuyBox>
  );
}
